import { lazy, Suspense, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlignLeft,
  ChevronDown,
  Clock3,
  Code2,
  Copy,
  GripVertical,
  Heading1,
  ImageIcon,
  List as ListIcon,
  Minus,
  Plus,
  Rows3,
  Trash2,
  Type,
} from 'lucide-react';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import { counterApi, type WpMediaItem } from '../../lib/api';
import { lazyRetry } from '../../lib/lazy-retry';
import { withPlainTextPaste } from '../../lib/quillPlainTextPaste';
import { COUNTER_TYPE_LABELS, type CounterModel } from '../../types/counter.types';
import { normalizeHexColor, prefixHexColor, safeColorForPicker } from './builder-v2/utils/color-utils';
import 'react-quill/dist/quill.snow.css';

const ReactQuill = lazy(() => lazyRetry(() => import('react-quill')));

export const PRODUCT_SHORT_DESCRIPTION_BUILDER_META_KEY = '_ef_product_short_description_builder';
export const MAX_PRODUCT_SHORT_DESCRIPTION_ELEMENTS = 10;

const CANVAS_ID = '__product_short_description_canvas__';

const QUILL_MODULES = withPlainTextPaste({
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote'],
    ['link', 'image'],
    ['clean'],
  ],
});

type StyleMode = 'default' | 'custom';
type DeviceAlign = 'left' | 'center' | 'right';
type HeadingLevel = 'h2' | 'h3' | 'h4';
type ListMarkerMode = 'none' | 'bullet' | 'icon';

export type ProductShortDescriptionElementType =
  | 'heading'
  | 'text'
  | 'list'
  | 'image'
  | 'accordion'
  | 'counter'
  | 'html'
  | 'separator'
  | 'spacer';

interface ProductShortDescriptionElementBase {
  id: string;
  type: ProductShortDescriptionElementType;
  marginTopDesktop: number;
  marginTopMobile: number;
}

interface ProductShortDescriptionHeadingElement extends ProductShortDescriptionElementBase {
  type: 'heading';
  content: string;
  level: HeadingLevel;
  highlight: boolean;
  highlightColor: string;
  highlightTextColor: string;
  styleMode: StyleMode;
  textColor: string;
  alignDesktop: DeviceAlign;
  alignMobile: DeviceAlign;
  fontSizeDesktop: number;
  fontSizeMobile: number;
  fontWeightDesktop: number;
  fontWeightMobile: number;
  lineHeightDesktop: number;
  lineHeightMobile: number;
}

interface ProductShortDescriptionTextElement extends ProductShortDescriptionElementBase {
  type: 'text';
  html: string;
  styleMode: StyleMode;
  textColor: string;
  alignDesktop: DeviceAlign;
  alignMobile: DeviceAlign;
  fontSizeDesktop: number;
  fontSizeMobile: number;
  fontWeightDesktop: number;
  fontWeightMobile: number;
  lineHeightDesktop: number;
  lineHeightMobile: number;
}

interface ProductShortDescriptionListElement extends ProductShortDescriptionElementBase {
  type: 'list';
  items: string[];
  markerMode: ListMarkerMode;
  iconUrl: string;
  styleMode: StyleMode;
  textColor: string;
  fontSizeDesktop: number;
  fontSizeMobile: number;
  fontWeightDesktop: number;
  fontWeightMobile: number;
  lineHeightDesktop: number;
  lineHeightMobile: number;
  itemSpacingDesktop: number;
  itemSpacingMobile: number;
  iconWidth: number;
}

interface ProductShortDescriptionImageElement extends ProductShortDescriptionElementBase {
  type: 'image';
  imageUrl: string;
  alt: string;
  linkUrl: string;
  styleMode: StyleMode;
  alignDesktop: DeviceAlign;
  alignMobile: DeviceAlign;
  maxWidthDesktop: number;
  maxWidthMobile: number;
  borderRadiusDesktop: number;
  borderRadiusMobile: number;
}

interface ProductShortDescriptionAccordionElement extends ProductShortDescriptionElementBase {
  type: 'accordion';
  title: string;
  html: string;
  styleMode: StyleMode;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  fontSizeDesktop: number;
  fontSizeMobile: number;
  fontWeightDesktop: number;
  fontWeightMobile: number;
}

interface ProductShortDescriptionCounterElement extends ProductShortDescriptionElementBase {
  type: 'counter';
  counterId: string;
}

interface ProductShortDescriptionHtmlElement extends ProductShortDescriptionElementBase {
  type: 'html';
  html: string;
}

interface ProductShortDescriptionSeparatorElement extends ProductShortDescriptionElementBase {
  type: 'separator';
  styleMode: StyleMode;
  color: string;
  widthDesktop: number;
  widthMobile: number;
  thicknessDesktop: number;
  thicknessMobile: number;
}

interface ProductShortDescriptionSpacerElement extends ProductShortDescriptionElementBase {
  type: 'spacer';
  height: number;
}

export type ProductShortDescriptionElement =
  | ProductShortDescriptionHeadingElement
  | ProductShortDescriptionTextElement
  | ProductShortDescriptionListElement
  | ProductShortDescriptionImageElement
  | ProductShortDescriptionAccordionElement
  | ProductShortDescriptionCounterElement
  | ProductShortDescriptionHtmlElement
  | ProductShortDescriptionSeparatorElement
  | ProductShortDescriptionSpacerElement;

type ProductShortDescriptionElementPatch = Partial<ProductShortDescriptionElementBase> & Record<string, unknown>;

interface ProductShortDescriptionBuilderSectionProps {
  integrationId?: string;
  value: ProductShortDescriptionElement[];
  onChange: (elements: ProductShortDescriptionElement[]) => void;
  description?: string;
  mediaPickerEnabled?: boolean;
}

const PALETTE: Array<{
  type: ProductShortDescriptionElementType;
  label: string;
  description: string;
  icon: typeof Type;
}> = [
  {
    type: 'heading',
    label: 'Nagłówek',
    description: 'Tytuł, obietnica lub wyróżnik',
    icon: Heading1,
  },
  {
    type: 'text',
    label: 'Tekst',
    description: 'Edytor WYSIWYG z formatowaniem',
    icon: Type,
  },
  {
    type: 'list',
    label: 'Lista',
    description: 'Bullet points z opcjonalną ikoną',
    icon: ListIcon,
  },
  {
    type: 'image',
    label: 'Grafika',
    description: 'Obraz z biblioteki mediów',
    icon: ImageIcon,
  },
  {
    type: 'accordion',
    label: 'Rozwijana belka',
    description: 'Pytanie lub sekcja po kliknięciu',
    icon: ChevronDown,
  },
  {
    type: 'counter',
    label: 'Licznik',
    description: 'Wybrany licznik FOMO',
    icon: Clock3,
  },
  {
    type: 'html',
    label: 'Kod HTML',
    description: 'Własny fragment HTML',
    icon: Code2,
  },
  {
    type: 'separator',
    label: 'Separator',
    description: 'Linia rozdzielająca treść',
    icon: Minus,
  },
  {
    type: 'spacer',
    label: 'Odstęp',
    description: 'Pusta przestrzeń desktop/mobile',
    icon: Rows3,
  },
];

function createElementId() {
  return `product-short-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createDefaultElement(type: ProductShortDescriptionElementType): ProductShortDescriptionElement {
  if (type === 'heading') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 14,
      marginTopMobile: 12,
      content: 'Obietnica marketingowa produktu',
      level: 'h2',
      highlight: false,
      highlightColor: '#f9db3e',
      highlightTextColor: '#1e1e1e',
      styleMode: 'default',
      textColor: '#1e1e1e',
      alignDesktop: 'left',
      alignMobile: 'left',
      fontSizeDesktop: 24,
      fontSizeMobile: 21,
      fontWeightDesktop: 700,
      fontWeightMobile: 700,
      lineHeightDesktop: 30,
      lineHeightMobile: 26,
    };
  }

  if (type === 'text') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 12,
      marginTopMobile: 10,
      html: '<p>Dodaj krótki opis produktu, najważniejsze korzyści i informacje, które klient powinien zobaczyć przed zakupem.</p>',
      styleMode: 'default',
      textColor: '#2a2a2a',
      alignDesktop: 'left',
      alignMobile: 'left',
      fontSizeDesktop: 16,
      fontSizeMobile: 15,
      fontWeightDesktop: 400,
      fontWeightMobile: 400,
      lineHeightDesktop: 26,
      lineHeightMobile: 24,
    };
  }

  if (type === 'list') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 12,
      marginTopMobile: 10,
      items: ['Najważniejsza korzyść produktu', 'Drugi argument zakupowy', 'Informacja, która domyka decyzję'],
      markerMode: 'none',
      iconUrl: '',
      styleMode: 'default',
      textColor: '#2a2a2a',
      fontSizeDesktop: 16,
      fontSizeMobile: 15,
      fontWeightDesktop: 400,
      fontWeightMobile: 400,
      lineHeightDesktop: 24,
      lineHeightMobile: 22,
      itemSpacingDesktop: 8,
      itemSpacingMobile: 7,
      iconWidth: 10,
    };
  }

  if (type === 'image') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 14,
      marginTopMobile: 12,
      imageUrl: '',
      alt: 'Grafika w krótkim opisie produktu',
      linkUrl: '',
      styleMode: 'default',
      alignDesktop: 'center',
      alignMobile: 'center',
      maxWidthDesktop: 100,
      maxWidthMobile: 100,
      borderRadiusDesktop: 8,
      borderRadiusMobile: 8,
    };
  }

  if (type === 'accordion') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 12,
      marginTopMobile: 10,
      title: 'Szczegóły produktu',
      html: '<p>Dodaj treść widoczną po rozwinięciu belki.</p>',
      styleMode: 'default',
      textColor: '#1e1e1e',
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      fontSizeDesktop: 15,
      fontSizeMobile: 14,
      fontWeightDesktop: 700,
      fontWeightMobile: 700,
    };
  }

  if (type === 'counter') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 12,
      marginTopMobile: 10,
      counterId: '',
    };
  }

  if (type === 'html') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 12,
      marginTopMobile: 10,
      html: '<div>Własny kod HTML</div>',
    };
  }

  if (type === 'separator') {
    return {
      id: createElementId(),
      type,
      marginTopDesktop: 16,
      marginTopMobile: 14,
      styleMode: 'default',
      color: '#e5e7eb',
      widthDesktop: 100,
      widthMobile: 100,
      thicknessDesktop: 1,
      thicknessMobile: 1,
    };
  }

  return {
    id: createElementId(),
    type,
    marginTopDesktop: 0,
    marginTopMobile: 0,
    height: 20,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback: number, min: number, max: number) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function asFontWeight(value: unknown, fallback: number) {
  return asNumber(value, fallback, 300, 900);
}

function asLineHeightPx(value: unknown, fallback: number, fontSize: number, min: number, max: number) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  const pxValue = n >= 100 ? Math.round(fontSize * (n / 100)) : n;
  return Math.min(max, Math.max(min, Math.round(pxValue)));
}

function asListItems(value: unknown, fallback: string[]) {
  if (!Array.isArray(value)) return fallback;
  return value
    .map((item) => asString(item))
    .slice(0, 20);
}

function asListMarkerMode(value: unknown, iconUrl: string): ListMarkerMode {
  if (value === 'none' || value === 'bullet' || value === 'icon') return value;
  return iconUrl.trim() ? 'icon' : 'none';
}

function asListIconWidth(value: Record<string, unknown>, fallback: number, styleMode: StyleMode) {
  if (value.iconWidth !== undefined) {
    return asNumber(value.iconWidth, fallback, 8, 64);
  }

  if (styleMode === 'custom' && value.iconSizeDesktop !== undefined) {
    return asNumber(value.iconSizeDesktop, fallback, 8, 64);
  }

  return fallback;
}

function asColor(value: unknown, fallback: string) {
  const text = asString(value, fallback);
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback;
}

function asStyleMode(value: unknown): StyleMode {
  return value === 'custom' ? 'custom' : 'default';
}

function asAlign(value: unknown, fallback: DeviceAlign): DeviceAlign {
  return value === 'center' || value === 'right' || value === 'left' ? value : fallback;
}

function asHeadingLevel(value: unknown): HeadingLevel {
  return value === 'h2' || value === 'h3' || value === 'h4' ? value : 'h2';
}

function isEmptyHtml(value: string) {
  const text = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return text.length === 0;
}

function normalizeShortDescriptionElement(value: unknown): ProductShortDescriptionElement | null {
  if (!isRecord(value)) return null;

  const type = value.type;
  if (
    type !== 'heading'
    && type !== 'text'
    && type !== 'list'
    && type !== 'image'
    && type !== 'accordion'
    && type !== 'counter'
    && type !== 'html'
    && type !== 'separator'
    && type !== 'spacer'
  ) {
    return null;
  }

  const fallbackBase = createDefaultElement(type);
  const base = type === 'spacer'
    ? {
        id: asString(value.id, createElementId()),
        marginTopDesktop: 0,
        marginTopMobile: 0,
      }
    : {
        id: asString(value.id, createElementId()),
        marginTopDesktop: asNumber(value.marginTopDesktop ?? value.marginTop, fallbackBase.marginTopDesktop, 0, 180),
        marginTopMobile: asNumber(value.marginTopMobile ?? value.marginTop, fallbackBase.marginTopMobile, 0, 180),
      };

  if (type === 'heading') {
    const fallback = createDefaultElement('heading') as ProductShortDescriptionHeadingElement;
    const fontSizeDesktop = asNumber(value.fontSizeDesktop, fallback.fontSizeDesktop, 8, 64);
    const fontSizeMobile = asNumber(value.fontSizeMobile, fallback.fontSizeMobile, 8, 48);
    return {
      ...fallback,
      ...base,
      content: asString(value.content, fallback.content),
      level: asHeadingLevel(value.level),
      highlight: value.highlight === true,
      highlightColor: asColor(value.highlightColor, fallback.highlightColor),
      highlightTextColor: asColor(value.highlightTextColor, fallback.highlightTextColor),
      styleMode: asStyleMode(value.styleMode),
      textColor: asColor(value.textColor, fallback.textColor),
      alignDesktop: asAlign(value.alignDesktop, fallback.alignDesktop),
      alignMobile: asAlign(value.alignMobile, fallback.alignMobile),
      fontSizeDesktop,
      fontSizeMobile,
      fontWeightDesktop: asFontWeight(value.fontWeightDesktop, fallback.fontWeightDesktop),
      fontWeightMobile: asFontWeight(value.fontWeightMobile, fallback.fontWeightMobile),
      lineHeightDesktop: asLineHeightPx(value.lineHeightDesktop, fallback.lineHeightDesktop, fontSizeDesktop, 8, 90),
      lineHeightMobile: asLineHeightPx(value.lineHeightMobile, fallback.lineHeightMobile, fontSizeMobile, 8, 80),
    };
  }

  if (type === 'text') {
    const fallback = createDefaultElement('text') as ProductShortDescriptionTextElement;
    const fontSizeDesktop = asNumber(value.fontSizeDesktop, fallback.fontSizeDesktop, 10, 32);
    const fontSizeMobile = asNumber(value.fontSizeMobile, fallback.fontSizeMobile, 10, 28);
    return {
      ...fallback,
      ...base,
      html: asString(value.html, fallback.html),
      styleMode: asStyleMode(value.styleMode),
      textColor: asColor(value.textColor, fallback.textColor),
      alignDesktop: asAlign(value.alignDesktop, fallback.alignDesktop),
      alignMobile: asAlign(value.alignMobile, fallback.alignMobile),
      fontSizeDesktop,
      fontSizeMobile,
      fontWeightDesktop: asFontWeight(value.fontWeightDesktop, fallback.fontWeightDesktop),
      fontWeightMobile: asFontWeight(value.fontWeightMobile, fallback.fontWeightMobile),
      lineHeightDesktop: asLineHeightPx(value.lineHeightDesktop, fallback.lineHeightDesktop, fontSizeDesktop, 12, 70),
      lineHeightMobile: asLineHeightPx(value.lineHeightMobile, fallback.lineHeightMobile, fontSizeMobile, 12, 64),
    };
  }

  if (type === 'list') {
    const fallback = createDefaultElement('list') as ProductShortDescriptionListElement;
    const fontSizeDesktop = asNumber(value.fontSizeDesktop, fallback.fontSizeDesktop, 10, 32);
    const fontSizeMobile = asNumber(value.fontSizeMobile, fallback.fontSizeMobile, 10, 28);
    const styleMode = asStyleMode(value.styleMode);
    return {
      ...fallback,
      ...base,
      iconUrl: asString(value.iconUrl),
      markerMode: asListMarkerMode(value.markerMode, asString(value.iconUrl)),
      items: asListItems(value.items, fallback.items),
      styleMode,
      textColor: asColor(value.textColor, fallback.textColor),
      fontSizeDesktop,
      fontSizeMobile,
      fontWeightDesktop: asFontWeight(value.fontWeightDesktop, fallback.fontWeightDesktop),
      fontWeightMobile: asFontWeight(value.fontWeightMobile, fallback.fontWeightMobile),
      lineHeightDesktop: asLineHeightPx(value.lineHeightDesktop, fallback.lineHeightDesktop, fontSizeDesktop, 12, 70),
      lineHeightMobile: asLineHeightPx(value.lineHeightMobile, fallback.lineHeightMobile, fontSizeMobile, 12, 64),
      itemSpacingDesktop: asNumber(value.itemSpacingDesktop, fallback.itemSpacingDesktop, 0, 48),
      itemSpacingMobile: asNumber(value.itemSpacingMobile, fallback.itemSpacingMobile, 0, 48),
      iconWidth: asListIconWidth(value, fallback.iconWidth, styleMode),
    };
  }

  if (type === 'image') {
    const fallback = createDefaultElement('image') as ProductShortDescriptionImageElement;
    return {
      ...fallback,
      ...base,
      imageUrl: asString(value.imageUrl),
      alt: asString(value.alt, fallback.alt),
      linkUrl: asString(value.linkUrl),
      styleMode: asStyleMode(value.styleMode),
      alignDesktop: asAlign(value.alignDesktop, fallback.alignDesktop),
      alignMobile: asAlign(value.alignMobile, fallback.alignMobile),
      maxWidthDesktop: asNumber(value.maxWidthDesktop, fallback.maxWidthDesktop, 10, 100),
      maxWidthMobile: asNumber(value.maxWidthMobile, fallback.maxWidthMobile, 10, 100),
      borderRadiusDesktop: asNumber(value.borderRadiusDesktop, fallback.borderRadiusDesktop, 0, 48),
      borderRadiusMobile: asNumber(value.borderRadiusMobile, fallback.borderRadiusMobile, 0, 48),
    };
  }

  if (type === 'accordion') {
    const fallback = createDefaultElement('accordion') as ProductShortDescriptionAccordionElement;
    return {
      ...fallback,
      ...base,
      title: asString(value.title, fallback.title),
      html: asString(value.html, fallback.html),
      styleMode: asStyleMode(value.styleMode),
      textColor: asColor(value.textColor, fallback.textColor),
      backgroundColor: asColor(value.backgroundColor, fallback.backgroundColor),
      borderColor: asColor(value.borderColor, fallback.borderColor),
      fontSizeDesktop: asNumber(value.fontSizeDesktop, fallback.fontSizeDesktop, 10, 28),
      fontSizeMobile: asNumber(value.fontSizeMobile, fallback.fontSizeMobile, 10, 24),
      fontWeightDesktop: asFontWeight(value.fontWeightDesktop, fallback.fontWeightDesktop),
      fontWeightMobile: asFontWeight(value.fontWeightMobile, fallback.fontWeightMobile),
    };
  }

  if (type === 'counter') {
    const fallback = createDefaultElement('counter') as ProductShortDescriptionCounterElement;
    return {
      ...fallback,
      ...base,
      counterId: asString(value.counterId, fallback.counterId),
    };
  }

  if (type === 'html') {
    const fallback = createDefaultElement('html') as ProductShortDescriptionHtmlElement;
    return {
      ...fallback,
      ...base,
      html: asString(value.html, fallback.html),
    };
  }

  if (type === 'separator') {
    const fallback = createDefaultElement('separator') as ProductShortDescriptionSeparatorElement;
    return {
      ...fallback,
      ...base,
      styleMode: asStyleMode(value.styleMode),
      color: asColor(value.color, fallback.color),
      widthDesktop: asNumber(value.widthDesktop, fallback.widthDesktop, 5, 100),
      widthMobile: asNumber(value.widthMobile, fallback.widthMobile, 5, 100),
      thicknessDesktop: asNumber(value.thicknessDesktop, fallback.thicknessDesktop, 1, 12),
      thicknessMobile: asNumber(value.thicknessMobile, fallback.thicknessMobile, 1, 12),
    };
  }

  const fallback = createDefaultElement('spacer') as ProductShortDescriptionSpacerElement;
  return {
    ...fallback,
    ...base,
    height: asNumber(value.height ?? value.heightDesktop ?? value.heightMobile, fallback.height, 0, 240),
  };
}

export function normalizeProductShortDescriptionElements(value: unknown): ProductShortDescriptionElement[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeShortDescriptionElement)
    .filter((element): element is ProductShortDescriptionElement => element !== null)
    .slice(0, MAX_PRODUCT_SHORT_DESCRIPTION_ELEMENTS);
}

export function parseProductShortDescriptionBuilderMeta(
  metaData: Array<{ key: string; value: unknown }> | undefined,
): ProductShortDescriptionElement[] {
  let raw: unknown;
  if (metaData) {
    for (let index = metaData.length - 1; index >= 0; index -= 1) {
      if (metaData[index].key === PRODUCT_SHORT_DESCRIPTION_BUILDER_META_KEY) {
        raw = metaData[index].value;
        break;
      }
    }
  }
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return normalizeProductShortDescriptionElements(raw);
  }

  if (typeof raw !== 'string') return [];

  try {
    return normalizeProductShortDescriptionElements(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function createShortDescriptionBuilderFromHtml(html: string): ProductShortDescriptionElement[] {
  if (!html || isEmptyHtml(html)) return [];
  const fallback = createDefaultElement('text') as ProductShortDescriptionTextElement;
  return [
    {
      ...fallback,
      marginTopDesktop: 0,
      marginTopMobile: 0,
      html,
    },
  ];
}

export function serializeProductShortDescriptionBuilder(elements: ProductShortDescriptionElement[]) {
  return JSON.stringify(normalizeProductShortDescriptionElements(elements));
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function styleVars(vars: Record<string, string | number | undefined>) {
  return Object.entries(vars)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}:${String(value)}`)
    .join(';');
}

function elementCssKey(element: ProductShortDescriptionElement) {
  const source = element.id || `${element.type}-${JSON.stringify(element)}`;
  const key = source.replace(/[^a-zA-Z0-9_-]/g, '');
  return key || `${element.type}-element`;
}

function commonStyleVars(element: ProductShortDescriptionElement) {
  if (element.type === 'spacer') {
    return {
      '--ef-sdb-mt-d': '0px',
      '--ef-sdb-mt-m': '0px',
    };
  }

  return {
    '--ef-sdb-mt-d': `${element.marginTopDesktop}px`,
    '--ef-sdb-mt-m': `${element.marginTopMobile}px`,
  };
}

function wrapElement(element: ProductShortDescriptionElement, innerHtml: string) {
  if (!innerHtml) return '';
  const style = styleVars(commonStyleVars(element));
  return `<div class="ef-sdb-element ef-sdb-element-${element.type} ef-sdb-el-${elementCssKey(element)}" style="${style}">${innerHtml}</div>`;
}

function renderProductShortDescriptionScopedCss(elements: ProductShortDescriptionElement[]) {
  const css = elements
    .filter((element): element is ProductShortDescriptionHeadingElement => element.type === 'heading' && element.styleMode === 'custom')
    .map((element) => {
      const selector = `.ef-short-description-builder .ef-sdb-el-${elementCssKey(element)} .ef-sdb-heading`;
      const textSelector = `${selector} .ef-sdb-heading-text`;
      const desktopColor = !element.highlight ? `color:${element.textColor} !important;` : '';
      return `${selector},${textSelector}{${desktopColor}text-align:${element.alignDesktop} !important;font-size:${element.fontSizeDesktop}px !important;font-weight:${element.fontWeightDesktop} !important;line-height:${element.lineHeightDesktop}px !important;}@media(max-width:768px){${selector},${textSelector}{text-align:${element.alignMobile} !important;font-size:${element.fontSizeMobile}px !important;font-weight:${element.fontWeightMobile} !important;line-height:${element.lineHeightMobile}px !important;}}`;
    })
    .join('');

  return css ? `<style class="ef-sdb-inline-styles">${css}</style>` : '';
}

export function renderProductShortDescriptionHtml(elements: ProductShortDescriptionElement[]) {
  const normalized = normalizeProductShortDescriptionElements(elements);
  const innerHtml = renderProductShortDescriptionScopedCss(normalized) + normalized.map((element) => {
    if (element.type === 'heading') {
      const Tag = element.level;
      const text = escapeHtml(element.content.trim());
      if (!text) return '';
      const custom = element.styleMode === 'custom';
      const levelNumber = Tag.replace('h', '');
      const headingColor = custom && !element.highlight ? element.textColor : undefined;
      const style = styleVars({
        color: headingColor,
        'text-align': custom ? `${element.alignDesktop} !important` : undefined,
        'font-size': custom ? `${element.fontSizeDesktop}px !important` : undefined,
        'font-weight': custom ? `${element.fontWeightDesktop} !important` : undefined,
        'line-height': custom ? `${element.lineHeightDesktop}px !important` : undefined,
        '--ef-sdb-heading-color': headingColor,
        '--ef-sdb-heading-align-d': custom ? element.alignDesktop : undefined,
        '--ef-sdb-heading-align-m': custom ? element.alignMobile : undefined,
        '--ef-sdb-heading-fs-d': custom ? `${element.fontSizeDesktop}px` : undefined,
        '--ef-sdb-heading-fs-m': custom ? `${element.fontSizeMobile}px` : undefined,
        '--ef-sdb-heading-fw-d': custom ? element.fontWeightDesktop : undefined,
        '--ef-sdb-heading-fw-m': custom ? element.fontWeightMobile : undefined,
        '--ef-sdb-heading-lh-d': custom ? `${element.lineHeightDesktop}px` : undefined,
        '--ef-sdb-heading-lh-m': custom ? `${element.lineHeightMobile}px` : undefined,
        '--ef-sdb-heading-highlight-bg': element.highlight ? element.highlightColor : undefined,
        '--ef-sdb-heading-highlight-color': element.highlight ? element.highlightTextColor : undefined,
      });
      const headingClass = `ef-sdb-heading${element.highlight ? ' is-highlighted' : ''}${custom ? ' is-custom' : ''}`;
      const headingTextStyle = styleVars({
        'font-size': custom ? `${element.fontSizeDesktop}px !important` : undefined,
        'font-weight': custom ? `${element.fontWeightDesktop} !important` : undefined,
        'line-height': custom ? `${element.lineHeightDesktop}px !important` : undefined,
        color: element.highlight ? `${element.highlightTextColor} !important` : undefined,
        'background-color': element.highlight ? `${element.highlightColor} !important` : undefined,
      });
      const headingTextStyleAttr = headingTextStyle ? ` style="${headingTextStyle}"` : '';
      const headingInner = `<span class="ef-sdb-heading-text"${headingTextStyleAttr}>${text}</span>`;
      const headingHtml = custom
        ? `<div class="${headingClass}" role="heading" aria-level="${levelNumber}" data-heading-level="${Tag}" style="${style}">${headingInner}</div>`
        : `<${Tag} class="${headingClass}" style="${style}">${headingInner}</${Tag}>`;
      return wrapElement(
        element,
        headingHtml,
      );
    }

    if (element.type === 'text') {
      if (!element.html || isEmptyHtml(element.html)) return '';
      const custom = element.styleMode === 'custom';
      const style = styleVars({
        '--ef-sdb-text-color': custom ? element.textColor : undefined,
        '--ef-sdb-text-align-d': custom ? element.alignDesktop : undefined,
        '--ef-sdb-text-align-m': custom ? element.alignMobile : undefined,
        '--ef-sdb-text-fs-d': custom ? `${element.fontSizeDesktop}px` : undefined,
        '--ef-sdb-text-fs-m': custom ? `${element.fontSizeMobile}px` : undefined,
        '--ef-sdb-text-fw-d': custom ? element.fontWeightDesktop : undefined,
        '--ef-sdb-text-fw-m': custom ? element.fontWeightMobile : undefined,
        '--ef-sdb-text-lh-d': custom ? `${element.lineHeightDesktop}px` : undefined,
        '--ef-sdb-text-lh-m': custom ? `${element.lineHeightMobile}px` : undefined,
      });
      return wrapElement(element, `<div class="ef-sdb-text${custom ? ' is-custom' : ''}" style="${style}">${element.html}</div>`);
    }

    if (element.type === 'list') {
      const items = element.items.map((item) => item.trim()).filter(Boolean);
      if (items.length === 0) return '';
      const custom = element.styleMode === 'custom';
      const hasIcon = element.markerMode === 'icon' && Boolean(element.iconUrl.trim());
      const noMarker = element.markerMode === 'none' || (element.markerMode === 'icon' && !hasIcon);
      const style = styleVars({
        '--ef-sdb-list-color': custom ? element.textColor : undefined,
        '--ef-sdb-list-fs-d': custom ? `${element.fontSizeDesktop}px` : undefined,
        '--ef-sdb-list-fs-m': custom ? `${element.fontSizeMobile}px` : undefined,
        '--ef-sdb-list-fw-d': custom ? element.fontWeightDesktop : undefined,
        '--ef-sdb-list-fw-m': custom ? element.fontWeightMobile : undefined,
        '--ef-sdb-list-lh-d': custom ? `${element.lineHeightDesktop}px` : undefined,
        '--ef-sdb-list-lh-m': custom ? `${element.lineHeightMobile}px` : undefined,
        '--ef-sdb-list-gap-d': custom ? `${element.itemSpacingDesktop}px` : undefined,
        '--ef-sdb-list-gap-m': custom ? `${element.itemSpacingMobile}px` : undefined,
        '--ef-sdb-list-icon-d': hasIcon ? `${element.iconWidth}px` : undefined,
        '--ef-sdb-list-icon-m': hasIcon ? `${element.iconWidth}px` : undefined,
      });
      const iconStyle = styleVars({
        '--ef-sdb-list-icon-d': hasIcon ? `${element.iconWidth}px` : undefined,
        '--ef-sdb-list-icon-m': hasIcon ? `${element.iconWidth}px` : undefined,
      });
      const icon = hasIcon
        ? `<span class="ef-sdb-list-icon" aria-hidden="true" style="${iconStyle}"><img src="${escapeHtml(element.iconUrl.trim())}" alt=""></span>`
        : '';
      const listItems = items
        .map((item) => `<li>${icon}<span class="ef-sdb-list-text">${escapeHtml(item)}</span></li>`)
        .join('');
      return wrapElement(
        element,
        `<ul class="ef-sdb-list marker-${element.markerMode}${hasIcon ? ' has-icon' : ''}${noMarker ? ' no-marker' : ''}${custom ? ' is-custom' : ''}" style="${style}">${listItems}</ul>`,
      );
    }

    if (element.type === 'image') {
      const imageUrl = element.imageUrl.trim();
      if (!imageUrl) return '';
      const custom = element.styleMode === 'custom';
      const style = styleVars({
        '--ef-sdb-image-align-d': custom ? element.alignDesktop : undefined,
        '--ef-sdb-image-align-m': custom ? element.alignMobile : undefined,
        '--ef-sdb-image-max-d': custom ? `${element.maxWidthDesktop}%` : undefined,
        '--ef-sdb-image-max-m': custom ? `${element.maxWidthMobile}%` : undefined,
        '--ef-sdb-image-radius-d': custom ? `${element.borderRadiusDesktop}px` : undefined,
        '--ef-sdb-image-radius-m': custom ? `${element.borderRadiusMobile}px` : undefined,
      });
      const image = `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(element.alt)}" loading="lazy">`;
      return wrapElement(
        element,
        `<div class="ef-sdb-image${custom ? ' is-custom' : ''}" style="${style}">${element.linkUrl.trim() ? `<a href="${escapeHtml(element.linkUrl.trim())}">${image}</a>` : image}</div>`,
      );
    }

    if (element.type === 'accordion') {
      const title = escapeHtml(element.title.trim());
      if (!title) return '';
      const custom = element.styleMode === 'custom';
      const style = styleVars({
        '--ef-sdb-accordion-color': custom ? element.textColor : undefined,
        '--ef-sdb-accordion-bg': custom ? element.backgroundColor : undefined,
        '--ef-sdb-accordion-border': custom ? element.borderColor : undefined,
        '--ef-sdb-accordion-fs-d': custom ? `${element.fontSizeDesktop}px` : undefined,
        '--ef-sdb-accordion-fs-m': custom ? `${element.fontSizeMobile}px` : undefined,
        '--ef-sdb-accordion-fw-d': custom ? element.fontWeightDesktop : undefined,
        '--ef-sdb-accordion-fw-m': custom ? element.fontWeightMobile : undefined,
      });
      return wrapElement(
        element,
        `<details class="ef-sdb-accordion${custom ? ' is-custom' : ''}" style="${style}"><summary><span>${title}</span><span class="ef-sdb-accordion-icon" aria-hidden="true"></span></summary><div class="ef-sdb-accordion-content">${element.html}</div></details>`,
      );
    }

    if (element.type === 'counter') {
      const counterId = element.counterId.trim();
      if (!counterId) return '';
      return wrapElement(element, `[ecomflow_counter id="${escapeHtml(counterId)}"]`);
    }

    if (element.type === 'html') {
      return wrapElement(element, `<div class="ef-sdb-html">${element.html}</div>`);
    }

    if (element.type === 'separator') {
      const custom = element.styleMode === 'custom';
      const style = styleVars({
        '--ef-sdb-separator-color': custom ? element.color : undefined,
        '--ef-sdb-separator-width-d': custom ? `${element.widthDesktop}%` : undefined,
        '--ef-sdb-separator-width-m': custom ? `${element.widthMobile}%` : undefined,
        '--ef-sdb-separator-thickness-d': custom ? `${element.thicknessDesktop}px` : undefined,
        '--ef-sdb-separator-thickness-m': custom ? `${element.thicknessMobile}px` : undefined,
      });
      return wrapElement(element, `<div class="ef-sdb-separator${custom ? ' is-custom' : ''}" style="${style}"><span></span></div>`);
    }

    const style = styleVars({
      '--ef-sdb-spacer-h': `${element.height}px`,
    });
    return wrapElement(element, `<div class="ef-sdb-spacer" style="${style}" aria-hidden="true"></div>`);
  }).join('');

  return innerHtml ? `<div class="ef-short-description-builder" data-ef-sdb="1">${innerHtml}</div>` : '';
}

function getElementTitle(element: ProductShortDescriptionElement) {
  if (element.type === 'heading') return `Nagłówek - ${element.content || 'tekst'}`;
  if (element.type === 'text') return 'Tekst';
  if (element.type === 'list') return 'Lista';
  if (element.type === 'image') return 'Grafika';
  if (element.type === 'accordion') return `Rozwijana belka - ${element.title || 'tytuł'}`;
  if (element.type === 'counter') return 'Licznik';
  if (element.type === 'html') return 'Kod HTML';
  if (element.type === 'separator') return 'Separator';
  return 'Odstęp';
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return <label htmlFor={htmlFor} className="block text-xs text-text-secondary mb-1.5">{children}</label>;
}

function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="input w-full text-sm"
      placeholder={placeholder}
    />
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isValidHex = (color: string) => /^#[0-9a-f]{6}$/i.test(color);

  const commitTextColor = (next: string) => {
    const prefixed = prefixHexColor(next);
    setDraft(prefixed);
    const normalized = normalizeHexColor(prefixed);
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  };

  const handleTextBlur = () => {
    const normalized = normalizeHexColor(draft);
    if (isValidHex(normalized)) {
      setDraft(normalized);
      onChange(normalized);
      return;
    }
    setDraft(value);
  };

  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={safeColorForPicker(value)}
          onChange={(event) => {
            setDraft(event.target.value);
            onChange(event.target.value);
          }}
          className="h-10 w-12 rounded-lg border border-border bg-surface-hover p-1"
        />
        <input
          type="text"
          value={draft}
          onChange={(event) => commitTextColor(event.target.value)}
          onBlur={handleTextBlur}
          className="input w-full text-sm font-mono"
          placeholder="#ededed"
        />
      </div>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  suffix,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commitDraft = (nextDraft: string) => {
    const numeric = Number(nextDraft);
    if (!Number.isFinite(numeric)) {
      setDraft(String(value));
      return;
    }

    const nextValue = Math.min(max, Math.max(min, Math.round(numeric)));
    onChange(nextValue);
    setDraft(String(nextValue));
  };

  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          value={draft}
          onChange={(event) => {
            const nextDraft = event.target.value;
            setDraft(nextDraft);
            if (nextDraft === '') return;

            const numeric = Number(nextDraft);
            if (Number.isFinite(numeric) && numeric >= min && numeric <= max) {
              onChange(Math.round(numeric));
            }
          }}
          onBlur={() => {
            if (draft === '') {
              setDraft(String(value));
              return;
            }
            commitDraft(draft);
          }}
          className="input w-full text-sm"
        />
        {suffix && <span className="text-xs text-text-muted w-8">{suffix}</span>}
      </div>
    </div>
  );
}

function AlignSelect({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: DeviceAlign;
  onChange: (value: DeviceAlign) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(asAlign(event.target.value, value))}
        className="input w-full text-sm"
      >
        <option value="left">Lewo</option>
        <option value="center">Środek</option>
        <option value="right">Prawo</option>
      </select>
    </div>
  );
}

function FontWeightSelect({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(asFontWeight(event.target.value, value))}
        className="input w-full text-sm"
      >
        <option value={300}>Lekka 300</option>
        <option value={400}>Normalna 400</option>
        <option value={500}>Średnia 500</option>
        <option value={600}>Półgruba 600</option>
        <option value={700}>Gruba 700</option>
        <option value={800}>Bardzo gruba 800</option>
        <option value={900}>Black 900</option>
      </select>
    </div>
  );
}

function StyleModeSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: StyleMode;
  onChange: (value: StyleMode) => void;
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>Stylowanie</FieldLabel>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(asStyleMode(event.target.value))}
        className="input w-full text-sm"
      >
        <option value="default">Domyślne</option>
        <option value="custom">Własne</option>
      </select>
    </div>
  );
}

function DevicePanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3 space-y-3">
      <p className="text-xs font-semibold text-text-secondary">{title}</p>
      {children}
    </div>
  );
}

function ElementSpacingFields({
  element,
  onUpdate,
}: {
  element: ProductShortDescriptionElement;
  onUpdate: (patch: ProductShortDescriptionElementPatch) => void;
}) {
  const id = (name: string) => `product-short-${element.id}-${name}`;

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3">
      <div className="flex items-center gap-2 mb-3">
        <AlignLeft className="w-3.5 h-3.5 text-accent-primary" />
        <p className="text-xs font-semibold text-text-secondary">Odstęp od poprzedniego elementu</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <NumberField
          id={id('marginTopDesktop')}
          label="Desktop"
          value={element.marginTopDesktop}
          min={0}
          max={180}
          suffix="px"
          onChange={(marginTopDesktop) => onUpdate({ marginTopDesktop })}
        />
        <NumberField
          id={id('marginTopMobile')}
          label="Mobile"
          value={element.marginTopMobile}
          min={0}
          max={180}
          suffix="px"
          onChange={(marginTopMobile) => onUpdate({ marginTopMobile })}
        />
      </div>
    </div>
  );
}

function RichTextEditor({
  value,
  onChange,
  minHeight = 120,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
  placeholder?: string;
}) {
  const modules = useMemo(() => QUILL_MODULES, []);

  return (
    <Suspense fallback={<div className="h-36 bg-surface-hover rounded-lg animate-pulse" />}>
      <div
        className="product-wysiwyg [&_.ql-editor]:min-h-[var(--ef-quill-min-height)] [&_.ql-editor]:text-sm [&_.ql-editor]:text-text-primary [&_.ql-editor.ql-blank::before]:!text-text-muted [&_.ql-container]:border-border [&_.ql-container]:bg-surface-hover [&_.ql-container]:rounded-b-lg [&_.ql-toolbar]:border-border [&_.ql-toolbar]:bg-surface-raised [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar_.ql-stroke]:stroke-gray-400 [&_.ql-toolbar_.ql-fill]:fill-gray-400 [&_.ql-toolbar_.ql-picker-label]:text-text-secondary [&_.ql-toolbar_.ql-picker-options]:bg-surface-hover [&_.ql-toolbar_.ql-picker-options]:border-border [&_.ql-toolbar_.ql-picker-item]:text-text-secondary"
        style={{ ['--ef-quill-min-height' as string]: `${minHeight}px` }}
      >
        <ReactQuill
          theme="snow"
          value={value}
          onChange={(content: string, _delta: unknown, source: string) => {
            if (source === 'user') onChange(content);
          }}
          modules={modules}
          placeholder={placeholder}
        />
      </div>
    </Suspense>
  );
}

function counterOptionLabel(counter: CounterModel) {
  return `${counter.name} (${COUNTER_TYPE_LABELS[counter.config.counterType]})`;
}

function selectedCounterName(counters: CounterModel[], counterId: string) {
  if (!counterId) return 'Wybierz licznik';
  return counters.find((counter) => counter.id === counterId)?.name ?? 'Wybrany licznik';
}

function ElementPreview({ element, counters = [] }: { element: ProductShortDescriptionElement; counters?: CounterModel[] }) {
  if (element.type === 'heading') {
    const customStyle = element.styleMode === 'custom'
      ? {
        color: element.textColor,
        textAlign: element.alignDesktop,
        fontSize: `${element.fontSizeDesktop}px`,
        fontWeight: element.fontWeightDesktop,
        lineHeight: `${element.lineHeightDesktop}px`,
      } as CSSProperties
      : undefined;

    return (
      <div className="text-sm font-bold text-text-primary" style={customStyle}>
        <span
          className={element.highlight ? 'px-2 py-0.5 box-decoration-clone' : ''}
          style={element.highlight ? { backgroundColor: element.highlightColor, color: element.highlightTextColor } : undefined}
        >
          {element.content || 'Nagłówek'}
        </span>
      </div>
    );
  }

  if (element.type === 'text') {
    return (
      <div
        className="text-xs text-text-secondary leading-relaxed line-clamp-3"
        dangerouslySetInnerHTML={{ __html: element.html || '<p>Tekst</p>' }}
      />
    );
  }

  if (element.type === 'list') {
    return (
      <ul className="space-y-1 text-xs text-text-secondary">
        {element.items.slice(0, 3).map((item, index) => (
          <li key={`${item}-${index}`} className={`flex items-center ${element.markerMode === 'none' ? 'gap-0' : 'gap-2'}`}>
            {element.markerMode === 'icon' && element.iconUrl ? (
              <span
                className="inline-flex shrink-0 items-center justify-center leading-none"
                style={{ width: `${element.iconWidth}px`, height: `${element.iconWidth}px` }}
              >
                <img src={element.iconUrl} alt="" className="block h-full w-full object-contain" />
              </span>
            ) : element.markerMode === 'bullet' ? (
              <span className="w-1.5 h-1.5 rounded-full bg-text-muted" />
            ) : null}
            <span className="truncate">{item || 'Punkt listy'}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (element.type === 'image') {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-base/60 overflow-hidden">
        {element.imageUrl ? (
          <img src={element.imageUrl} alt={element.alt} className="w-full max-h-28 object-cover" />
        ) : (
          <div className="h-24 flex items-center justify-center text-text-muted">
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'accordion') {
    return (
      <div className="rounded-lg border border-border-subtle bg-surface-base/60 px-3 py-2">
        <div className="flex items-center justify-between gap-2 text-xs font-semibold text-text-primary">
          <span className="truncate">{element.title || 'Rozwijana belka'}</span>
          <ChevronDown className="w-3.5 h-3.5 text-text-muted" />
        </div>
      </div>
    );
  }

  if (element.type === 'counter') {
    return (
      <div className="rounded-lg border border-accent-primary/20 bg-accent-primary/5 px-3 py-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-text-primary">
          <Clock3 className="h-3.5 w-3.5 text-accent-primary" />
          <span className="truncate">{selectedCounterName(counters, element.counterId)}</span>
        </div>
        <p className="mt-1 truncate font-mono text-[10px] text-text-muted">
          {element.counterId ? `[ecomflow_counter id="${element.counterId}"]` : 'Shortcode zostanie zapisany po wybraniu licznika'}
        </p>
      </div>
    );
  }

  if (element.type === 'html') {
    return (
      <pre className="max-h-20 overflow-hidden rounded-lg border border-border-subtle bg-surface-base/60 p-2 text-[10px] leading-relaxed text-text-muted">
        {element.html || '<div>HTML</div>'}
      </pre>
    );
  }

  if (element.type === 'separator') {
    return (
      <div className="py-3">
        <div className="h-px bg-border-strong" />
      </div>
    );
  }

  return (
    <div className="h-8 rounded-lg border border-dashed border-border-subtle bg-surface-base/50 flex items-center justify-center text-[11px] text-text-muted">
      Odstęp {element.height}px
    </div>
  );
}

function PaletteItem({
  item,
  disabled,
  onAdd,
}: {
  item: (typeof PALETTE)[number];
  disabled: boolean;
  onAdd: () => void;
}) {
  const Icon = item.icon;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `short-palette-${item.type}`,
    data: { source: 'palette', type: item.type },
    disabled,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
  };

  return (
    <button
      ref={setNodeRef}
      type="button"
      disabled={disabled}
      onClick={onAdd}
      style={style}
      {...attributes}
      {...listeners}
      className={`w-full text-left rounded-xl border p-3 transition-all ${
        disabled
          ? 'border-border-subtle bg-surface-base/40 opacity-50 cursor-not-allowed'
          : 'border-border bg-surface-raised/40 hover:border-accent-primary/40 hover:bg-accent-primary/5 cursor-grab active:cursor-grabbing'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-text-primary">{item.label}</span>
          <span className="block text-[11px] leading-snug text-text-muted">{item.description}</span>
        </span>
      </div>
    </button>
  );
}

function CanvasDropZone({
  children,
  isEmpty,
  isFull,
  isDragActive,
}: {
  children: ReactNode;
  isEmpty: boolean;
  isFull: boolean;
  isDragActive: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: CANVAS_ID });
  const isDropReady = isDragActive && !isFull;
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[260px] rounded-xl border-2 border-dashed p-3 transition-all ${
        isOver && !isFull
          ? 'border-accent-primary bg-accent-primary/5'
          : isDropReady
            ? 'border-accent-primary/50 bg-accent-primary/[0.03] ring-1 ring-accent-primary/20'
            : 'border-border-subtle bg-surface-base/40'
      }`}
    >
      {isEmpty ? (
        <div className="h-full min-h-[230px] flex flex-col items-center justify-center text-center text-text-muted">
          <Plus className="w-7 h-7 mb-2 text-accent-primary/60" />
          <p className="text-sm font-medium text-text-secondary">Brak elementów krótkiego opisu</p>
          <p className="text-xs mt-1">Dodaj komponent z listy obok</p>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function SortableElementCard({
  element,
  counters,
  selected,
  canDuplicate,
  onSelect,
  onDuplicate,
  onRemove,
}: {
  element: ProductShortDescriptionElement;
  counters: CounterModel[];
  selected: boolean;
  canDuplicate: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
    data: { source: 'canvas' },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded-xl border bg-surface-raised/60 p-3 transition-all ${
        selected ? 'border-accent-primary ring-2 ring-accent-primary/20' : 'border-border hover:border-border-strong'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" onClick={onSelect} className="min-w-0 flex-1 text-left">
          <span className="block text-xs font-semibold text-text-primary truncate">{getElementTitle(element)}</span>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover cursor-grab active:cursor-grabbing"
            title="Przeciągnij"
          >
            <GripVertical className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onDuplicate();
            }}
            disabled={!canDuplicate}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-muted"
            title={canDuplicate ? 'Duplikuj' : 'Limit elementów'}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10"
            title="Usuń"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <button type="button" onClick={onSelect} className="w-full text-left">
        <ElementPreview element={element} counters={counters} />
      </button>
    </div>
  );
}

function HeadingStyleFields({
  element,
  onUpdate,
  fieldId,
}: {
  element: ProductShortDescriptionHeadingElement;
  onUpdate: (patch: ProductShortDescriptionElementPatch) => void;
  fieldId: (name: string) => string;
}) {
  if (element.styleMode !== 'custom') return null;

  return (
    <div className="space-y-3">
      {!element.highlight && (
        <ColorField id={fieldId('textColor')} label="Kolor tekstu" value={element.textColor} onChange={(textColor) => onUpdate({ textColor })} />
      )}
      <HeadingHighlightFields element={element} onUpdate={onUpdate} fieldId={fieldId} />
      <div className="grid sm:grid-cols-2 gap-3">
        <DevicePanel title="Desktop">
          <NumberField id={fieldId('fontSizeDesktop')} label="Rozmiar" value={element.fontSizeDesktop} min={8} max={64} suffix="px" onChange={(fontSizeDesktop) => onUpdate({ fontSizeDesktop })} />
          <FontWeightSelect id={fieldId('fontWeightDesktop')} label="Grubość" value={element.fontWeightDesktop} onChange={(fontWeightDesktop) => onUpdate({ fontWeightDesktop })} />
          <NumberField id={fieldId('lineHeightDesktop')} label="Interlinia" value={element.lineHeightDesktop} min={8} max={90} suffix="px" onChange={(lineHeightDesktop) => onUpdate({ lineHeightDesktop })} />
          <AlignSelect id={fieldId('alignDesktop')} label="Wyrównanie" value={element.alignDesktop} onChange={(alignDesktop) => onUpdate({ alignDesktop })} />
        </DevicePanel>
        <DevicePanel title="Mobile">
          <NumberField id={fieldId('fontSizeMobile')} label="Rozmiar" value={element.fontSizeMobile} min={8} max={48} suffix="px" onChange={(fontSizeMobile) => onUpdate({ fontSizeMobile })} />
          <FontWeightSelect id={fieldId('fontWeightMobile')} label="Grubość" value={element.fontWeightMobile} onChange={(fontWeightMobile) => onUpdate({ fontWeightMobile })} />
          <NumberField id={fieldId('lineHeightMobile')} label="Interlinia" value={element.lineHeightMobile} min={8} max={80} suffix="px" onChange={(lineHeightMobile) => onUpdate({ lineHeightMobile })} />
          <AlignSelect id={fieldId('alignMobile')} label="Wyrównanie" value={element.alignMobile} onChange={(alignMobile) => onUpdate({ alignMobile })} />
        </DevicePanel>
      </div>
    </div>
  );
}

function HeadingHighlightFields({
  element,
  onUpdate,
  fieldId,
}: {
  element: ProductShortDescriptionHeadingElement;
  onUpdate: (patch: ProductShortDescriptionElementPatch) => void;
  fieldId: (name: string) => string;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3 space-y-3">
      <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={element.highlight}
          onChange={(event) => onUpdate({ highlight: event.target.checked })}
          className="w-4 h-4 rounded border-border-strong bg-surface-raised text-accent-primary focus:ring-accent-primary/30"
        />
        Wyróżnij nagłówek tłem
      </label>
      {element.highlight && (
        <div className="grid sm:grid-cols-2 gap-3">
          <ColorField id={fieldId('highlightColor')} label="Kolor wyróżnienia" value={element.highlightColor} onChange={(highlightColor) => onUpdate({ highlightColor })} />
          <ColorField id={fieldId('highlightTextColor')} label="Kolor tekstu w wyróżnieniu" value={element.highlightTextColor} onChange={(highlightTextColor) => onUpdate({ highlightTextColor })} />
        </div>
      )}
    </div>
  );
}

function ElementConfigPanel({
  element,
  counters,
  onUpdate,
  onPickImage,
  onRemoveImage,
  mediaPickerEnabled,
}: {
  element: ProductShortDescriptionElement;
  counters: CounterModel[];
  onUpdate: (patch: ProductShortDescriptionElementPatch) => void;
  onPickImage: () => void;
  onRemoveImage: () => void;
  mediaPickerEnabled: boolean;
}) {
  const fieldId = (name: string) => `product-short-${element.id}-${name}`;

  if (element.type === 'heading') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div className="grid sm:grid-cols-[minmax(0,1fr)_150px] gap-3">
          <div>
            <FieldLabel htmlFor={fieldId('content')}>Treść nagłówka</FieldLabel>
            <TextInput id={fieldId('content')} value={element.content} onChange={(content) => onUpdate({ content })} placeholder="Nagłówek" />
          </div>
          <div>
            <FieldLabel htmlFor={fieldId('level')}>Poziom</FieldLabel>
            <select
              id={fieldId('level')}
              value={element.level}
              onChange={(event) => onUpdate({ level: asHeadingLevel(event.target.value) })}
              className="input w-full text-sm"
            >
              <option value="h2">H2</option>
              <option value="h3">H3</option>
              <option value="h4">H4</option>
            </select>
          </div>
        </div>

        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode !== 'custom' && (
          <HeadingHighlightFields element={element} onUpdate={onUpdate} fieldId={fieldId} />
        )}
        <HeadingStyleFields element={element} onUpdate={onUpdate} fieldId={fieldId} />
      </div>
    );
  }

  if (element.type === 'text') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('html')}>Tekst</FieldLabel>
          <RichTextEditor value={element.html} onChange={(html) => onUpdate({ html })} placeholder="Wpisz tekst krótkiego opisu..." />
        </div>
        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode === 'custom' && (
          <div className="space-y-3">
            <ColorField id={fieldId('textColor')} label="Kolor tekstu" value={element.textColor} onChange={(textColor) => onUpdate({ textColor })} />
            <div className="grid sm:grid-cols-2 gap-3">
              <DevicePanel title="Desktop">
                <NumberField id={fieldId('fontSizeDesktop')} label="Rozmiar" value={element.fontSizeDesktop} min={10} max={32} suffix="px" onChange={(fontSizeDesktop) => onUpdate({ fontSizeDesktop })} />
                <FontWeightSelect id={fieldId('fontWeightDesktop')} label="Grubość" value={element.fontWeightDesktop} onChange={(fontWeightDesktop) => onUpdate({ fontWeightDesktop })} />
                <NumberField id={fieldId('lineHeightDesktop')} label="Interlinia" value={element.lineHeightDesktop} min={12} max={70} suffix="px" onChange={(lineHeightDesktop) => onUpdate({ lineHeightDesktop })} />
                <AlignSelect id={fieldId('alignDesktop')} label="Wyrównanie" value={element.alignDesktop} onChange={(alignDesktop) => onUpdate({ alignDesktop })} />
              </DevicePanel>
              <DevicePanel title="Mobile">
                <NumberField id={fieldId('fontSizeMobile')} label="Rozmiar" value={element.fontSizeMobile} min={10} max={28} suffix="px" onChange={(fontSizeMobile) => onUpdate({ fontSizeMobile })} />
                <FontWeightSelect id={fieldId('fontWeightMobile')} label="Grubość" value={element.fontWeightMobile} onChange={(fontWeightMobile) => onUpdate({ fontWeightMobile })} />
                <NumberField id={fieldId('lineHeightMobile')} label="Interlinia" value={element.lineHeightMobile} min={12} max={64} suffix="px" onChange={(lineHeightMobile) => onUpdate({ lineHeightMobile })} />
                <AlignSelect id={fieldId('alignMobile')} label="Wyrównanie" value={element.alignMobile} onChange={(alignMobile) => onUpdate({ alignMobile })} />
              </DevicePanel>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'list') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('items')}>Punkty listy</FieldLabel>
          <div id={fieldId('items')} className="space-y-2">
            {element.items.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item}
                  onChange={(event) => {
                    const next = [...element.items];
                    next[index] = event.target.value;
                    onUpdate({ items: next });
                  }}
                  className="input w-full text-sm"
                  placeholder={`Punkt ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => onUpdate({ items: element.items.filter((_, itemIndex) => itemIndex !== index) })}
                  className="p-2 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10"
                  title="Usuń punkt"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onUpdate({ items: [...element.items, ''] })}
            className="btn-secondary text-sm inline-flex items-center gap-2 mt-3"
          >
            <Plus className="w-3.5 h-3.5" />
            Dodaj punkt
          </button>
        </div>

        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3 space-y-3">
          <div>
            <p className="text-sm font-semibold text-text-primary">Globalna ikonka bulletu</p>
            <p className="text-xs text-text-muted mt-0.5">Wybierz sposób oznaczania punktów listy.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {([
              { value: 'none', label: 'Bez ikonki', description: 'Tylko tekst' },
              { value: 'bullet', label: 'Kropka', description: 'Standardowy punkt' },
              { value: 'icon', label: 'Własna ikonka', description: 'Wczytaj grafikę' },
            ] as Array<{ value: ListMarkerMode; label: string; description: string }>).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onUpdate({ markerMode: option.value })}
                className={`rounded-lg border px-3 py-2 text-left transition-all ${
                  element.markerMode === option.value
                    ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                    : 'border-border-subtle bg-surface-hover/50 text-text-secondary hover:text-text-primary hover:border-border'
                }`}
              >
                <span className="block text-xs font-semibold">{option.label}</span>
                <span className="block text-[10px] mt-0.5 opacity-80">{option.description}</span>
              </button>
            ))}
          </div>
          {element.markerMode === 'icon' && (
            <div className="grid gap-3 rounded-lg border border-border-subtle bg-surface-hover/40 p-3 sm:grid-cols-[minmax(0,1fr)_150px]">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-16 h-16 rounded-lg border border-border-subtle bg-surface-hover flex items-center justify-center overflow-hidden shrink-0">
                  {element.iconUrl ? (
                    <img
                      src={element.iconUrl}
                      alt="Ikonka listy"
                      className="block object-contain"
                      style={{ width: `${element.iconWidth}px`, height: `${element.iconWidth}px` }}
                    />
                  ) : (
                    <ListIcon className="w-5 h-5 text-text-muted" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={onPickImage}
                    disabled={!mediaPickerEnabled}
                    className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    title={mediaPickerEnabled ? undefined : 'Biblioteka mediów wymaga integracji sklepu'}
                  >
                    {element.iconUrl ? 'Zmień ikonkę' : 'Wybierz ikonkę'}
                  </button>
                  {element.iconUrl && (
                    <button type="button" onClick={onRemoveImage} className="ml-3 text-xs text-text-muted hover:text-danger">
                      Usuń ikonkę
                    </button>
                  )}
                </div>
              </div>
              <NumberField id={fieldId('iconWidth')} label="Szerokość ikonki" value={element.iconWidth} min={8} max={64} suffix="px" onChange={(iconWidth) => onUpdate({ iconWidth })} />
              <div className="sm:col-span-2">
                <FieldLabel htmlFor={fieldId('iconUrl')}>URL ikonki</FieldLabel>
                <TextInput
                  id={fieldId('iconUrl')}
                  value={element.iconUrl}
                  onChange={(iconUrl) => onUpdate({ iconUrl, markerMode: iconUrl.trim() ? 'icon' : element.markerMode })}
                  placeholder="https://..."
                  type="url"
                />
              </div>
            </div>
          )}
        </div>

        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode === 'custom' && (
          <div className="space-y-3">
            <ColorField id={fieldId('textColor')} label="Kolor tekstu" value={element.textColor} onChange={(textColor) => onUpdate({ textColor })} />
            <div className="grid sm:grid-cols-2 gap-3">
              <DevicePanel title="Desktop">
                <NumberField id={fieldId('fontSizeDesktop')} label="Rozmiar" value={element.fontSizeDesktop} min={10} max={32} suffix="px" onChange={(fontSizeDesktop) => onUpdate({ fontSizeDesktop })} />
                <FontWeightSelect id={fieldId('fontWeightDesktop')} label="Grubość" value={element.fontWeightDesktop} onChange={(fontWeightDesktop) => onUpdate({ fontWeightDesktop })} />
                <NumberField id={fieldId('lineHeightDesktop')} label="Interlinia" value={element.lineHeightDesktop} min={12} max={70} suffix="px" onChange={(lineHeightDesktop) => onUpdate({ lineHeightDesktop })} />
                <NumberField id={fieldId('itemSpacingDesktop')} label="Odstęp między punktami" value={element.itemSpacingDesktop} min={0} max={48} suffix="px" onChange={(itemSpacingDesktop) => onUpdate({ itemSpacingDesktop })} />
              </DevicePanel>
              <DevicePanel title="Mobile">
                <NumberField id={fieldId('fontSizeMobile')} label="Rozmiar" value={element.fontSizeMobile} min={10} max={28} suffix="px" onChange={(fontSizeMobile) => onUpdate({ fontSizeMobile })} />
                <FontWeightSelect id={fieldId('fontWeightMobile')} label="Grubość" value={element.fontWeightMobile} onChange={(fontWeightMobile) => onUpdate({ fontWeightMobile })} />
                <NumberField id={fieldId('lineHeightMobile')} label="Interlinia" value={element.lineHeightMobile} min={12} max={64} suffix="px" onChange={(lineHeightMobile) => onUpdate({ lineHeightMobile })} />
                <NumberField id={fieldId('itemSpacingMobile')} label="Odstęp między punktami" value={element.itemSpacingMobile} min={0} max={48} suffix="px" onChange={(itemSpacingMobile) => onUpdate({ itemSpacingMobile })} />
              </DevicePanel>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'image') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-3">
          {element.imageUrl ? (
            <img src={element.imageUrl} alt={element.alt} className="w-full max-h-56 object-contain rounded-lg bg-surface-hover" />
          ) : (
            <div className="h-40 flex items-center justify-center rounded-lg border border-dashed border-border-subtle text-text-muted">
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
          <div className="flex items-center gap-3 mt-3">
            <button
              type="button"
              onClick={onPickImage}
              disabled={!mediaPickerEnabled}
              className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title={mediaPickerEnabled ? undefined : 'Biblioteka mediów wymaga integracji sklepu'}
            >
              {element.imageUrl ? 'Zmień grafikę' : 'Wybierz grafikę'}
            </button>
            {element.imageUrl && (
              <button type="button" onClick={onRemoveImage} className="text-xs text-text-muted hover:text-danger">
                Usuń grafikę
              </button>
            )}
          </div>
        </div>
        <div>
          <FieldLabel htmlFor={fieldId('imageUrl')}>URL grafiki</FieldLabel>
          <TextInput id={fieldId('imageUrl')} value={element.imageUrl} onChange={(imageUrl) => onUpdate({ imageUrl })} placeholder="https://..." type="url" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel htmlFor={fieldId('alt')}>Alt tekst</FieldLabel>
            <TextInput id={fieldId('alt')} value={element.alt} onChange={(alt) => onUpdate({ alt })} placeholder="Opis grafiki" />
          </div>
          <div>
            <FieldLabel htmlFor={fieldId('linkUrl')}>Link po kliknięciu</FieldLabel>
            <TextInput id={fieldId('linkUrl')} value={element.linkUrl} onChange={(linkUrl) => onUpdate({ linkUrl })} placeholder="https://..." type="url" />
          </div>
        </div>
        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode === 'custom' && (
          <div className="grid sm:grid-cols-2 gap-3">
            <DevicePanel title="Desktop">
              <AlignSelect id={fieldId('alignDesktop')} label="Wyrównanie" value={element.alignDesktop} onChange={(alignDesktop) => onUpdate({ alignDesktop })} />
              <NumberField id={fieldId('maxWidthDesktop')} label="Maks. szerokość" value={element.maxWidthDesktop} min={10} max={100} suffix="%" onChange={(maxWidthDesktop) => onUpdate({ maxWidthDesktop })} />
              <NumberField id={fieldId('borderRadiusDesktop')} label="Zaokrąglenie" value={element.borderRadiusDesktop} min={0} max={48} suffix="px" onChange={(borderRadiusDesktop) => onUpdate({ borderRadiusDesktop })} />
            </DevicePanel>
            <DevicePanel title="Mobile">
              <AlignSelect id={fieldId('alignMobile')} label="Wyrównanie" value={element.alignMobile} onChange={(alignMobile) => onUpdate({ alignMobile })} />
              <NumberField id={fieldId('maxWidthMobile')} label="Maks. szerokość" value={element.maxWidthMobile} min={10} max={100} suffix="%" onChange={(maxWidthMobile) => onUpdate({ maxWidthMobile })} />
              <NumberField id={fieldId('borderRadiusMobile')} label="Zaokrąglenie" value={element.borderRadiusMobile} min={0} max={48} suffix="px" onChange={(borderRadiusMobile) => onUpdate({ borderRadiusMobile })} />
            </DevicePanel>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'accordion') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('title')}>Tytuł belki</FieldLabel>
          <TextInput id={fieldId('title')} value={element.title} onChange={(title) => onUpdate({ title })} placeholder="Tytuł belki" />
        </div>
        <div>
          <FieldLabel htmlFor={fieldId('html')}>Treść po rozwinięciu</FieldLabel>
          <RichTextEditor value={element.html} onChange={(html) => onUpdate({ html })} placeholder="Treść belki..." />
        </div>
        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode === 'custom' && (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <ColorField id={fieldId('textColor')} label="Tekst" value={element.textColor} onChange={(textColor) => onUpdate({ textColor })} />
              <ColorField id={fieldId('backgroundColor')} label="Tło" value={element.backgroundColor} onChange={(backgroundColor) => onUpdate({ backgroundColor })} />
              <ColorField id={fieldId('borderColor')} label="Obramowanie" value={element.borderColor} onChange={(borderColor) => onUpdate({ borderColor })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <DevicePanel title="Desktop">
                <NumberField id={fieldId('fontSizeDesktop')} label="Rozmiar" value={element.fontSizeDesktop} min={10} max={28} suffix="px" onChange={(fontSizeDesktop) => onUpdate({ fontSizeDesktop })} />
                <FontWeightSelect id={fieldId('fontWeightDesktop')} label="Grubość" value={element.fontWeightDesktop} onChange={(fontWeightDesktop) => onUpdate({ fontWeightDesktop })} />
              </DevicePanel>
              <DevicePanel title="Mobile">
                <NumberField id={fieldId('fontSizeMobile')} label="Rozmiar" value={element.fontSizeMobile} min={10} max={24} suffix="px" onChange={(fontSizeMobile) => onUpdate({ fontSizeMobile })} />
                <FontWeightSelect id={fieldId('fontWeightMobile')} label="Grubość" value={element.fontWeightMobile} onChange={(fontWeightMobile) => onUpdate({ fontWeightMobile })} />
              </DevicePanel>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'counter') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('counterId')}>Wybierz licznik</FieldLabel>
          <select
            id={fieldId('counterId')}
            value={element.counterId}
            onChange={(event) => onUpdate({ counterId: event.target.value })}
            className="input w-full text-sm"
          >
            <option value="">Wybierz licznik</option>
            {counters.map((counter) => (
              <option key={counter.id} value={counter.id}>{counterOptionLabel(counter)}</option>
            ))}
          </select>
          {counters.length === 0 ? (
            <p className="mt-2 text-xs text-text-muted">Najpierw utwórz licznik w zakładce Liczniki.</p>
          ) : (
            <p className="mt-2 text-xs text-text-muted">W tym miejscu krótkiego opisu zostanie osadzony wybrany licznik FOMO.</p>
          )}
        </div>
        {element.counterId && (
          <div className="rounded-lg border border-border-subtle bg-surface-base/50 p-3">
            <p className="mb-1 text-xs font-medium text-text-secondary">Zapisany shortcode</p>
            <code className="block break-all rounded-md bg-surface-hover px-2 py-1 font-mono text-xs text-text-primary">
              {`[ecomflow_counter id="${element.counterId}"]`}
            </code>
          </div>
        )}
      </div>
    );
  }

  if (element.type === 'html') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <div>
          <FieldLabel htmlFor={fieldId('html')}>Kod HTML</FieldLabel>
          <textarea
            id={fieldId('html')}
            value={element.html}
            onChange={(event) => onUpdate({ html: event.target.value })}
            className="input w-full font-mono text-xs"
            rows={9}
            placeholder="<div>...</div>"
          />
        </div>
      </div>
    );
  }

  if (element.type === 'separator') {
    return (
      <div className="space-y-4">
        <ElementSpacingFields element={element} onUpdate={onUpdate} />
        <StyleModeSelect id={fieldId('styleMode')} value={element.styleMode} onChange={(styleMode) => onUpdate({ styleMode })} />
        {element.styleMode === 'custom' && (
          <div className="space-y-3">
            <ColorField id={fieldId('color')} label="Kolor separatora" value={element.color} onChange={(color) => onUpdate({ color })} />
            <div className="grid sm:grid-cols-2 gap-3">
              <DevicePanel title="Desktop">
                <NumberField id={fieldId('widthDesktop')} label="Szerokość" value={element.widthDesktop} min={5} max={100} suffix="%" onChange={(widthDesktop) => onUpdate({ widthDesktop })} />
                <NumberField id={fieldId('thicknessDesktop')} label="Grubość" value={element.thicknessDesktop} min={1} max={12} suffix="px" onChange={(thicknessDesktop) => onUpdate({ thicknessDesktop })} />
              </DevicePanel>
              <DevicePanel title="Mobile">
                <NumberField id={fieldId('widthMobile')} label="Szerokość" value={element.widthMobile} min={5} max={100} suffix="%" onChange={(widthMobile) => onUpdate({ widthMobile })} />
                <NumberField id={fieldId('thicknessMobile')} label="Grubość" value={element.thicknessMobile} min={1} max={12} suffix="px" onChange={(thicknessMobile) => onUpdate({ thicknessMobile })} />
              </DevicePanel>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NumberField id={fieldId('height')} label="Wysokość" value={element.height} min={0} max={240} suffix="px" onChange={(height) => onUpdate({ height, marginTopDesktop: 0, marginTopMobile: 0 })} />
    </div>
  );
}

export function ProductShortDescriptionBuilderSection({
  integrationId,
  value,
  onChange,
  description = 'Komponenty zapisują się jako krótki opis produktu i konfiguracja w meta produktu.',
  mediaPickerEnabled = Boolean(integrationId),
}: ProductShortDescriptionBuilderSectionProps) {
  const elements = value.slice(0, MAX_PRODUCT_SHORT_DESCRIPTION_ELEMENTS);
  const [selectedId, setSelectedId] = useState<string | null>(elements[0]?.id ?? null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [mediaTargetId, setMediaTargetId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const { data: counters = [] } = useQuery({
    queryKey: ['counters', integrationId],
    queryFn: () => counterApi.listCounters(integrationId || '').then((response) => response.data as CounterModel[]),
    enabled: Boolean(integrationId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (selectedId && elements.some((element) => element.id === selectedId)) return;
    setSelectedId(elements[0]?.id ?? null);
  }, [elements, selectedId]);

  const selectedElement = useMemo(
    () => elements.find((element) => element.id === selectedId) ?? elements[0] ?? null,
    [elements, selectedId],
  );
  const isFull = elements.length >= MAX_PRODUCT_SHORT_DESCRIPTION_ELEMENTS;

  const commitElements = (next: ProductShortDescriptionElement[]) => {
    onChange(normalizeProductShortDescriptionElements(next));
  };

  const addElement = (type: ProductShortDescriptionElementType) => {
    if (isFull) return;
    const defaults = createDefaultElement(type);
    const element = type === 'spacer'
      ? defaults
      : {
          ...defaults,
          marginTopDesktop: elements.length === 0 ? 15 : Math.max(defaults.marginTopDesktop, 10),
          marginTopMobile: elements.length === 0 ? 12 : Math.max(defaults.marginTopMobile, 10),
        };
    if (type === 'counter' && counters.length > 0) {
      (element as ProductShortDescriptionCounterElement).counterId = counters[0].id;
    }
    commitElements([...elements, element]);
    setSelectedId(element.id);
  };

  const updateElement = (id: string, patch: ProductShortDescriptionElementPatch) => {
    commitElements(elements.map((element) => (
      element.id === id ? ({ ...element, ...patch } as ProductShortDescriptionElement) : element
    )));
  };

  const removeElement = (id: string) => {
    const index = elements.findIndex((element) => element.id === id);
    const next = elements.filter((element) => element.id !== id);
    commitElements(next);
    if (selectedId === id) {
      setSelectedId(next[index]?.id ?? next[index - 1]?.id ?? null);
    }
  };

  const duplicateElement = (id: string) => {
    if (isFull) return;
    const index = elements.findIndex((element) => element.id === id);
    if (index < 0) return;

    const duplicate = normalizeShortDescriptionElement({
      ...elements[index],
      id: createElementId(),
      marginTopDesktop: elements[index].type === 'spacer' ? 0 : Math.max(elements[index].marginTopDesktop, 10),
      marginTopMobile: elements[index].type === 'spacer' ? 0 : Math.max(elements[index].marginTopMobile, 10),
    });
    if (!duplicate) return;

    const next = [...elements];
    next.splice(index + 1, 0, duplicate);
    commitElements(next);
    setSelectedId(duplicate.id);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const source = active.data.current?.source;
    const type = active.data.current?.type as ProductShortDescriptionElementType | undefined;
    const overId = String(over.id);
    const overCanvas = overId === CANVAS_ID || elements.some((element) => element.id === overId);

    if (source === 'palette' && type && overCanvas) {
      addElement(type);
      return;
    }

    if (source === 'canvas' && active.id !== over.id) {
      const oldIndex = elements.findIndex((element) => element.id === active.id);
      const newIndex = elements.findIndex((element) => element.id === over.id);
      if (oldIndex >= 0 && newIndex >= 0) {
        commitElements(arrayMove(elements, oldIndex, newIndex));
      }
    }
  };

  const activePaletteItem = PALETTE.find((item) => `short-palette-${item.type}` === activeDragId);
  const ActivePaletteIcon = activePaletteItem?.icon;
  const activeElement = elements.find((element) => element.id === activeDragId);

  const handleMediaSelect = (url: string, item?: WpMediaItem) => {
    if (!mediaTargetId) return;
    const target = elements.find((element) => element.id === mediaTargetId);
    if (!target) return;

    if (target.type === 'image') {
      updateElement(target.id, {
        imageUrl: url,
        alt: item?.alt || item?.name || target.alt,
      });
    }

    if (target.type === 'list') {
      updateElement(target.id, { iconUrl: url, markerMode: 'icon' });
    }

    setMediaTargetId(null);
  };

  const handleRemoveImage = () => {
    if (!selectedElement) return;
    if (selectedElement.type === 'image') {
      updateElement(selectedElement.id, { imageUrl: '' });
    }
    if (selectedElement.type === 'list') {
      updateElement(selectedElement.id, { iconUrl: '', markerMode: 'none' });
    }
  };

  return (
    <div className="rounded-xl border border-border bg-surface-raised/30 p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-text-primary">Kreator krótkiego opisu</p>
          <p className="text-xs text-text-muted mt-1">{description}</p>
        </div>
        <span className={`text-xs font-medium ${isFull ? 'text-warning' : 'text-text-muted'}`}>
          {elements.length}/{MAX_PRODUCT_SHORT_DESCRIPTION_ELEMENTS}
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div className="grid gap-4 lg:grid-cols-[230px_minmax(0,1fr)]">
          <div className="space-y-2">
            {PALETTE.map((item) => (
              <PaletteItem key={item.type} item={item} disabled={isFull} onAdd={() => addElement(item.type)} />
            ))}
          </div>

          <CanvasDropZone isEmpty={elements.length === 0} isFull={isFull} isDragActive={Boolean(activeDragId)}>
            <SortableContext items={elements.map((element) => element.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {elements.map((element) => (
                  <SortableElementCard
                    key={element.id}
                    element={element}
                    counters={counters}
                    selected={selectedElement?.id === element.id}
                    canDuplicate={!isFull}
                    onSelect={() => setSelectedId(element.id)}
                    onDuplicate={() => duplicateElement(element.id)}
                    onRemove={() => removeElement(element.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </CanvasDropZone>
        </div>

        <DragOverlay dropAnimation={null}>
          {activePaletteItem && ActivePaletteIcon ? (
            <div className="w-56 rounded-xl border border-accent-primary bg-surface-raised p-3 shadow-lg">
              <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <ActivePaletteIcon className="w-4 h-4 text-accent-primary" />
                {activePaletteItem.label}
              </div>
            </div>
          ) : activeElement ? (
            <div className="w-72 rounded-xl border border-accent-primary bg-surface-raised p-3 shadow-lg">
              <ElementPreview element={activeElement} counters={counters} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {selectedElement ? (
        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-sm font-semibold text-text-primary">{getElementTitle(selectedElement)}</p>
              <p className="text-xs text-text-muted mt-0.5">Konfiguracja wybranego komponentu</p>
            </div>
          </div>
          <ElementConfigPanel
            element={selectedElement}
            counters={counters}
            onUpdate={(patch) => updateElement(selectedElement.id, patch)}
            onPickImage={() => setMediaTargetId(selectedElement.id)}
            onRemoveImage={handleRemoveImage}
            mediaPickerEnabled={mediaPickerEnabled}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-border-subtle bg-surface-base/40 p-4 text-sm text-text-muted">
          Kliknij komponent na canvasie, aby go skonfigurować.
        </div>
      )}

      {mediaTargetId && mediaPickerEnabled && integrationId && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onSelect={handleMediaSelect}
          onClose={() => setMediaTargetId(null)}
        />
      )}
    </div>
  );
}
